'use client';

import { c as _c } from "react/compiler-runtime";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { DEMOGRAPHICS_AGE_GROUP_OPTIONS, DEMOGRAPHICS_GENDER_MIX_OPTIONS } from '@sizzlenz/shared';
import type { InitiativeDemographics } from '@sizzlenz/shared';
import { MultiSelectDropdown } from '@/components/ui/multi-select-dropdown';
interface DemographicsSectionProps {
  value: InitiativeDemographics;
  onChange: (value: InitiativeDemographics) => void;
}
const AUDIENCE_TYPE_PRESETS: Record<string, {
  male: number;
  female: number;
  other: number;
}> = {
  mostly_female: {
    male: 20,
    female: 70,
    other: 10
  },
  mostly_male: {
    male: 70,
    female: 20,
    other: 10
  },
  balanced: {
    male: 45,
    female: 45,
    other: 10
  },
  diverse: {
    male: 33,
    female: 33,
    other: 34
  }
};
export function DemographicsSection(t0) {
  const $ = _c(41);
  const {
    value,
    onChange
  } = t0;
  const totalPercentage = value.genderSplit.male + value.genderSplit.female + value.genderSplit.other;
  let t1;
  if ($[0] !== onChange || $[1] !== value) {
    t1 = type => {
      if (!type) {
        onChange({
          ...value,
          gender: "",
          genderSplit: {
            male: 0,
            female: 0,
            other: 0
          }
        });
        return;
      }
      const preset = AUDIENCE_TYPE_PRESETS[type];
      onChange({
        ...value,
        gender: type,
        genderSplit: preset || value.genderSplit
      });
    };
    $[0] = onChange;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleGenderChange = t1;
  let t2;
  if ($[3] !== onChange || $[4] !== value) {
    t2 = (gender, newValue) => {
      const clamped = Math.max(0, Math.min(100, newValue));
      onChange({
        ...value,
        genderSplit: {
          ...value.genderSplit,
          [gender]: clamped
        }
      });
    };
    $[3] = onChange;
    $[4] = value;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleGenderSplitChange = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Label htmlFor="numberOfAttendees">Expected Attendees</Label>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = value.numberOfAttendees ?? "";
  let t5;
  if ($[7] !== onChange || $[8] !== value) {
    t5 = e => onChange({
      ...value,
      numberOfAttendees: e.target.value ? Number(e.target.value) : undefined
    });
    $[7] = onChange;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="space-y-2">{t3}<Input id="numberOfAttendees" type="number" min={0} value={t4} onChange={t5} placeholder="e.g. 50" /></div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Label>Age Groups</Label>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = DEMOGRAPHICS_AGE_GROUP_OPTIONS.map(_temp);
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== onChange || $[16] !== value) {
    t9 = ids => onChange({
      ...value,
      ageGroups: ids
    });
    $[15] = onChange;
    $[16] = value;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9 || $[19] !== value.ageGroups) {
    t10 = <div className="space-y-2">{t7}<MultiSelectDropdown label={null} placeholder="Select age groups..." options={t8} selectedIds={value.ageGroups} onSelectionChange={t9} showChips={true} showSummaryInTrigger={true} showBulkActions={false} /></div>;
    $[18] = t9;
    $[19] = value.ageGroups;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6) {
    t11 = <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{t6}{t10}</div>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Label>Gender</Label>;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== handleGenderChange || $[26] !== value.gender) {
    t13 = DEMOGRAPHICS_GENDER_MIX_OPTIONS.map(option => {
      const isSelected = value.gender === option.id;
      return <button key={option.id} type="button" onClick={() => handleGenderChange(option.id)} className={cn("px-3 py-2 rounded-lg border text-sm font-medium transition-all cursor-pointer", isSelected ? "border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500" : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50")}>{option.label}</button>;
    });
    $[25] = handleGenderChange;
    $[26] = value.gender;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <div className="space-y-2">{t12}<div className="grid grid-cols-2 md:grid-cols-4 gap-2">{t13}</div></div>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== handleGenderSplitChange || $[31] !== totalPercentage || $[32] !== value.gender || $[33] !== value.genderSplit.female || $[34] !== value.genderSplit.male || $[35] !== value.genderSplit.other) {
    t15 = value.gender && <div className="space-y-3"><div className="flex items-center justify-between"><Label className="text-xs">Split</Label><span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", totalPercentage === 100 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700")}>{totalPercentage}%</span></div><div className="space-y-3"><div className="flex items-center gap-3"><span className="text-sm text-gray-600 w-14">Male</span><div className="flex-1"><Slider value={[value.genderSplit.male]} onValueChange={t16 => {
              const [v] = t16;
              return handleGenderSplitChange("male", v);
            }} max={100} step={1} className="cursor-pointer" /></div><div className="flex items-center gap-1 w-16"><Input type="number" min={0} max={100} value={value.genderSplit.male} onChange={e_0 => handleGenderSplitChange("male", Number(e_0.target.value))} className="h-7 w-12 text-xs text-center px-1" /><span className="text-xs text-gray-400">%</span></div></div><div className="flex items-center gap-3"><span className="text-sm text-gray-600 w-14">Female</span><div className="flex-1"><Slider value={[value.genderSplit.female]} onValueChange={t17 => {
              const [v_0] = t17;
              return handleGenderSplitChange("female", v_0);
            }} max={100} step={1} className="cursor-pointer" /></div><div className="flex items-center gap-1 w-16"><Input type="number" min={0} max={100} value={value.genderSplit.female} onChange={e_1 => handleGenderSplitChange("female", Number(e_1.target.value))} className="h-7 w-12 text-xs text-center px-1" /><span className="text-xs text-gray-400">%</span></div></div><div className="flex items-center gap-3"><span className="text-sm text-gray-600 w-14">Other</span><div className="flex-1"><Slider value={[value.genderSplit.other]} onValueChange={t18 => {
              const [v_1] = t18;
              return handleGenderSplitChange("other", v_1);
            }} max={100} step={1} className="cursor-pointer" /></div><div className="flex items-center gap-1 w-16"><Input type="number" min={0} max={100} value={value.genderSplit.other} onChange={e_2 => handleGenderSplitChange("other", Number(e_2.target.value))} className="h-7 w-12 text-xs text-center px-1" /><span className="text-xs text-gray-400">%</span></div></div></div></div>;
    $[30] = handleGenderSplitChange;
    $[31] = totalPercentage;
    $[32] = value.gender;
    $[33] = value.genderSplit.female;
    $[34] = value.genderSplit.male;
    $[35] = value.genderSplit.other;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t11 || $[38] !== t14 || $[39] !== t15) {
    t16 = <div className="space-y-5">{t11}{t14}{t15}</div>;
    $[37] = t11;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
}
function _temp(o) {
  return {
    id: o.id,
    label: o.label
  };
}